import { useSelector } from 'react-redux'
import { DateTime } from 'luxon'
import { useServiceProxy } from './useServiceProxy'
import { selectCurrentUser, selectTicket } from 'redux/appStore'
import { useCreateEhiTicketNote } from 'hooks/tickets'
import { useVehicleLocation, useVehicleDestinationLocation } from 'hooks/redux/useLocations'
import { LocationPayload } from 'types/location'
import { isNil } from 'lodash-es'
import { FleetVEMRequest } from 'types/kong'
import { isProduction } from 'config'
import { EventTypes, EventTypeLabels } from 'types/events'

const TOW_SERVICES = [EventTypes.ACCIDENT, EventTypes.EXCHANGE, EventTypes.RECOVERY, EventTypes.TOW]

function generateVemNote(
  eventType: EventTypes,
  vehicleDestinationLocation?: LocationPayload | null,
  vehicleLocation?: LocationPayload | null,
) {
  const title = 'A Roadside Service has been dispatched for this vehicle'
  const anyQuestions = 'If you have questions please email RoadsideAssistance@em.com'

  let serviceType: string | undefined
  let address: string | undefined

  const isSoftService = !TOW_SERVICES.includes(eventType)
  if (isSoftService) {
    // SOFT SERVICES
    serviceType = EventTypeLabels[eventType]
    address = vehicleLocation?.addressDescription?.replace('<br/>', ', ')
  } else if (!isSoftService) {
    // TOW SERVICES
    serviceType = EventTypeLabels[EventTypes.TOW]
    address = vehicleDestinationLocation?.addressDescription?.replace('<br/>', ', ')
  } else {
    serviceType = ''
  }
  if (isNil(address)) return `${title} - ${serviceType} - ${anyQuestions}`
  return `${title} - ${serviceType} - ${address} - ${anyQuestions}`
}

export function useFleetVehicleVEMNote() {
  const proxy = useServiceProxy()
  const ticket = useSelector(selectTicket)
  const currentUser = useSelector(selectCurrentUser)
  const createTicketNote = useCreateEhiTicketNote()
  const vehicleDestinationLocation = useVehicleDestinationLocation()
  const vehicleLocation = useVehicleLocation()

  const vehicleUnitNumber = ticket.vehicle?.unitNumber

  return async function createVEMNote(eventType: EventTypes) {
    const vemNote = generateVemNote(eventType, vehicleDestinationLocation, vehicleLocation)
    const originIdentity = currentUser?.nameID
    const requestBody: FleetVEMRequest = {
      type: `urn:com.ehi:${isProduction ? 'prd' : 'xqa'}:reference:vehicle:noteType:OTHER`,
      content: vemNote,
    }

    try {
      const response = await proxy(
        'post',
        `/serviceproxy/fleetVehicle/${vehicleUnitNumber}/note`,
        { 'Ehi-Origin-Identity': originIdentity },
        requestBody,
      )
      if (response.status < 400) await createTicketNote(`VEM Note Added ${vemNote}`)
    } catch (e) {
      console.error(e)
    }
  }
}
